import React from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router-dom'

import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import FilterLinesIcon from 'client/utils/icons/filter-lines-icon'
import { OrdersContext } from 'contexts/OrdersContext'
import { LogOutIcon } from 'lucide-react'
import moment from 'moment'
import {
  DcBreadcrumbs,
  DcButton,
  DcDateRangePicker,
  DcDiv,
  DcDropDown,
  DcFlexBox,
  DcInput,
  DcOverlaySlider,
  DcTag,
  PixelCurrency,
  PixelDate,
  PixelPageContainer,
  PixelTable,
  PixelText,
  Section,
  SectionContent,
  colors,
} from 'pixel-base'
import queryString from 'query-string'
import { useApi } from 'shared/hooks'
import { get } from 'utils/api-routes.util'
import { SectionActions } from 'app/common/styles/section'
import { IDispatch } from '../../../../decl'

interface CourierList {
  courier_name: string
  courier_id: string | null
}
interface WarehouseList {
  name: string
  wh_id: number | string
}
const breadcrumbs = [
  {
    name: 'Orders',
    active: false,
    link: '/orders',
  },
  {
    name: 'Order Dispatches',
    active: true,
  },
]

const CourierLoadSheet = () => {
  const navigate = useNavigate()
  // const currentDate = new Date()
  // const start_date = new Date(currentDate)
  // start_date.setDate(1)
  // const end_date = new Date(currentDate)
  const [showMobileFiltersModal, setShowMobileFiltersModal] =
    React.useState(false)
  const [data, setData] = React.useState([])
  const [tableLoading, setTableLoading] = React.useState(false)
  const [courierList, setCourierList] = React.useState([])
  const [warehouseList, setWarehouseList] = React.useState([])
  const [selectedRows, setSelectedRows] = React.useState([])
  const [selectedDateRange, setSelectedDateRange] = React.useState({
    startDate: '',
    endDate: '',
  })
  const { state } = React.useContext(OrdersContext)
  React.useEffect(() => {
    setCourierList(state?.couriers)
    setWarehouseList(state?.warehouses)
  }, [state?.warehouses, state?.courier])
  const [filters, setFilters] = React.useState<object>({
    page: 0,
    status: 'null',
    page_size: 50,
    warehouse_id: 'null',
    courier_id: 'null',
  })
  const [totalRows, setTotalRows] = React.useState(0)

  const [resetPaginationToggle] = React.useState(false)

  useApi<{ data: IDispatch[] }>(
    () => get('/courier/loadsheet/dispatch', filters),
    {
      onSuccess: (res) => {
        const response = res as {
          data: IDispatch[]
          _meta: { total: number }
        }
        setData(response?.data || [])
        setTotalRows(response?.total || 0)
        setTableLoading(false)
      },
      skip: !filters,
      deps: [filters],
    },
  )

  const handlePageChange = (page) => {
    if (selectedDateRange.startDate) {
      const updatedFilters = {
        ...filters,
        page: page - 1,
        start_date: moment(selectedDateRange.startDate).format('YYYY-MM-DD'),
        end_date: moment(selectedDateRange.endDate).format('YYYY-MM-DD'),
      }
      setFilters(updatedFilters)
    }
  }
  const onRowClicked = (row) => {
    navigate(`/orders/courier-load-sheet/details/dispatch/${row.dispatch_id}`)
  }
  const handlePerRowsChange = async (newPerPage) => {
    setFilters({
      ...filters,
      page_size: newPerPage,
    })
  }

  function convertToCSV(arr) {
    const header = Object.keys(arr[0]).join(',')
    const rows = arr.map((obj) => Object.values(obj).join(','))
    return `${header}\n${rows.join('\n')}`
  }
  function downloadCSV(csv) {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const csvURL = URL.createObjectURL(csvData)
    const tempLink = document.createElement('a')
    tempLink.href = csvURL
    tempLink.setAttribute('download', 'Dispatches.csv')
    tempLink.click()
  }
  const columns = [
    {
      dataKey: 'dispatch_time',
      name: <PixelText multiLine>Creation Date</PixelText>,
      width: '12%',
      sortable: true,
      selector: (row) => (
        <PixelDate
          className="textSmall"
          value={row.dispatch_time}
          format={'dateWithTime'}
        />
      ),
    },
    {
      dataKey: 'courier_name',
      name: 'Courier',
      sortable: true,
      selector: (row) => <PixelText multiLine>{row.courier_name}</PixelText>,
    },
    {
      dataKey: 'dispatch_name',
      name: <PixelText multiLine>Dispatch Name</PixelText>,
      sortable: true,
      selector: (row) => <PixelText multiLine>{row.dispatch_name}</PixelText>,
    },
    {
      dataKey: 'total_packages',
      name: <PixelText>Packages</PixelText>,
      sortable: true,
      selector: (row) => row.total_packages,
    },
    {
      dataKey: 'total_pieces',
      name: <PixelText>Pieces</PixelText>,
      sortable: true,
      selector: (row) => row.total_pieces,
    },
    {
      dataKey: 'total_cod_amount',
      name: <PixelText>COD Amount</PixelText>,
      sortable: true,
      selector: (row) => (
        <PixelCurrency hideSymbol={true} value={row.total_cod_amount} />
      ),
    },
    {
      dataKey: 'dispatch_id',
      name: <PixelText multiLine>Dispatch Id</PixelText>,
      sortable: true,
      selector: (row) => row.dispatch_id,
    },
    {
      dataKey: 'dispatch_status',
      name: <PixelText>Status</PixelText>,
      sortable: true,
      selector: (row) => (
        <DcTag
          textColor={
            row.dispatch_status === 'open'
              ? colors.warning
              : row.dispatch_status === 'packaging'
              ? colors.primary
              : row.dispatch_status === 'ready to ship'
              ? colors.success
              : row.dispatch_status === 'shipped'
              ? colors.danger
              : colors.secondary
          }
          customBackgroundColor={
            row.dispatch_status === 'open'
              ? colors.warningLight
              : row.dispatch_status === 'packaging'
              ? colors.primaryLight
              : row.dispatch_status === 'ready to ship'
              ? colors.successLight
              : row.dispatch_status === 'shipped'
              ? colors.dangerLight
              : colors.secondaryLight
          }
        >
          {row.dispatch_status}
        </DcTag>
      ),
    },
    {
      dataKey: 'warehouse_name',
      name: <PixelText>Warehouse</PixelText>,
      sortable: true,
      selector: (row) => (
        <PixelText multiLine>
          {row.warehouse_name ? row.warehouse_name : '-'}
        </PixelText>
      ),
    },
  ]
  const handleSelectedRowsChange = (rows) => {
    setSelectedRows(rows.selectedRows)
  }

  const exportSelectedRows = () => {
    if (selectedRows.length === 0) {
      const csv = convertToCSV(data)
      downloadCSV(csv)
      return
    }
    const csv = convertToCSV(selectedRows)
    downloadCSV(csv)
  }
  const handleDateRangeApply = (dateRange) => {
    if (dateRange) {
      const { startDate, endDate } = dateRange
      const formattedStartDate = moment(startDate).format('YYYY-MM-DD')
      const formattedEndDate = moment(endDate).format('YYYY-MM-DD')

      const updatedFilters = {
        ...filters,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      }

      navigate({
        pathname: '/orders/courier-load-sheet',
        search: queryString.stringify(updatedFilters),
      })

      setFilters(updatedFilters)
    }
  }
  const MobileFiltersModal = () => {
    return (
      <DcFlexBox
        padding={isMobile ? '10px' : '5px 0px'}
        height="auto"
        justifyContent={isMobile ? 'center' : 'space-between'}
        flexDirection={isMobile ? 'column' : 'row'}
      >
        <DcFlexBox
          width={isMobile ? '100%' : '60%'}
          gap="5px"
          style={{
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <DcDropDown
            dropdownSize="sm"
            placeholder="Select Status"
            options={[
              {
                label: 'Select Status',
                value: null,
              },
              {
                label: 'Open',
                value: 'open',
              },
              {
                label: 'Packaging',
                value: 'packaging',
              },
              {
                label: 'Ready to Ship',
                value: 'ready-to-ship',
              },
              {
                label: 'Shipped',
                value: 'shipped',
              },
              {
                label: 'Closed',
                value: 'closed',
              },
            ]}
            value={filters?.status}
            onChange={(e) => {
              setFilters({
                ...filters,
                status: e.target.value,
              })
            }}
          />
          <DcDropDown
            dropdownSize="sm"
            required
            placeholder="Select Courier"
            options={[
              {
                courier_name: 'Select Courier',
                courier_id: 'null',
              },
              ...courierList,
            ].map((p: CourierList) => {
              return {
                label: p.courier_name,
                value: p.courier_id,
              }
            })}
            value={filters.courier_id}
            onChange={(e) => {
              setFilters({
                ...filters,
                courier_id: e.target.value,
              })
            }}
          />
          <DcDropDown
            dropdownSize="sm"
            required
            placeholder={'Select Warehouse'}
            options={[
              { name: 'Select Warehouse', wh_id: 'null' },
              ...warehouseList,
            ].map((data: WarehouseList) => {
              return {
                label: data.name,
                value: data.wh_id,
              }
            })}
            value={filters.warehouse_id}
            onChange={(e) => {
              setFilters({
                ...filters,
                warehouse_id: e.target.value,
              })
            }}
          />
        </DcFlexBox>
        <SectionActions
          style={{
            display: 'flex',
            justifyContent: isMobile ? 'flex-start' : 'flex-end',
            flexDirection: isMobile ? 'row-reverse' : 'row',
          }}
        >
          {' '}
          <DcDiv
            style={
              !isMobile
                ? { minWidth: '245px' }
                : {
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '10px 0px',
                  }
            }
          >
            <DcDateRangePicker
              size="sm"
              month={2}
              ranges={{
                start_date: selectedDateRange.startDate
                  ? new Date(selectedDateRange.startDate)
                  : '',
                end_date: selectedDateRange.endDate
                  ? new Date(selectedDateRange.endDate)
                  : '',
              }}
              onApply={() => handleDateRangeApply(selectedDateRange)}
              onChange={(e) => {
                setSelectedDateRange({
                  ...selectedDateRange,
                  startDate: e.range1.startDate,
                  endDate: e.range1.endDate,
                })
              }}
            />
          </DcDiv>
          &nbsp; &nbsp;
          <DcButton
            size="sm"
            variant="outline-secondary"
            onClick={exportSelectedRows}
            withIcon={true}
          >
            <LogOutIcon size={16} />
            <span>Export</span>
          </DcButton>
        </SectionActions>
      </DcFlexBox>
    )
  }
  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <SectionActions
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          position: 'relative',
          width: '100%',
        }}
      >
        <DcInput
          showsearchicon={0}
          inputSize={isMobile ? 'medium' : 'small'}
          style={{ backgroundColor: colors.grayLighter }}
          maxLength={20}
          width={isMobile ? '100%' : '350px'}
          placeholder="Search"
          name="search"
          onChange={(ev) => {
            setFilters({
              ...filters,
              search: ev.target.value,
            })
          }}
          value={filters?.search}
        />
        {isMobile && (
          <DcDiv position="absolute" right="0px" top="2px">
            <DcButton
              variant="link"
              onClick={() => setShowMobileFiltersModal(true)}
            >
              <FilterLinesIcon size={16} />
            </DcButton>
          </DcDiv>
        )}
      </SectionActions>
    )
  }, [
    filters,
    selectedDateRange,
    setSelectedDateRange,
    exportSelectedRows,
    handleDateRangeApply,
  ])
  return (
    <DcFlexBox flexDirection="column">
      <DcFlexBox
        padding="0px 20px"
        height="60px"
        style={{
          justifyContent: 'space-between',
          borderBottom: `1px solid ${colors.grayBorderLight}`,
        }}
        alignItems="center"
      >
        <DcFlexBox alignItems="center" width="auto">
          <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />
          <SectionActions />
        </DcFlexBox>
        <DcDiv>
          <ProfileSection />
        </DcDiv>
      </DcFlexBox>

      <PixelPageContainer style={{ padding: '0px' }}>
        {!isMobile && (
          <Section
            style={{
              padding: '0px',
            }}
          >
            <SectionContent
              style={
                isMobile
                  ? { padding: '5px', borderRadius: '0px' }
                  : { padding: '0px 20px', borderRadius: '0px' }
              }
            >
              {MobileFiltersModal()}
              {/* {isMobile && (
              <DcButton
              onClick={() => setShowMobileFiltersModal(true)}
              variant="outline-secondary"
                size="sm"
              >
              Filters
              </DcButton>
              )} */}
            </SectionContent>
          </Section>
        )}
        <Section style={{ padding: '20px' }}>
          <SectionContent style={{ padding: '10px' }}>
            <PixelTable
              pagination={true}
              columns={columns}
              progressPending={tableLoading}
              data={data}
              paginationResetDefaultPage={resetPaginationToggle}
              onChangeRowsPerPage={handlePerRowsChange}
              fixedHeader
              selectableRows
              paginationServer
              searchServer
              paginationTotalRows={totalRows}
              highlightOnHover={true}
              selectableRowsHighlight
              onSelectedRowsChange={handleSelectedRowsChange}
              isSearchable={isMobile ? false : true}
              pointerOnHover={true}
              paginationPerPage={50}
              onChangePage={handlePageChange}
              subHeader={true}
              subHeaderComponent={subHeaderComponentMemo}
              onRowClicked={onRowClicked}
              paginationRowsPerPageOptions={[50, 100, 250, 500]}
              fixedHeaderScrollHeight={
                isMobile ? 'calc(100vh - 230px)' : 'calc(100vh - 270px)'
              }
              customStyles={{
                subHeader: {
                  style: {
                    padding: '0px !important',
                  },
                },
              }}
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>
      <DcOverlaySlider
        isOpen={showMobileFiltersModal}
        onClose={() => setShowMobileFiltersModal(false)}
        title="Filters"
        customWith="50%"
        padding="0px"
      >
        {MobileFiltersModal()}
      </DcOverlaySlider>
    </DcFlexBox>
  )
}

export default CourierLoadSheet
